/** @format */

/* eslint @typescript-eslint/prefer-nullish-coalescing: "warn" */
import isPropValid from '@emotion/is-prop-valid';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import {
    Icon,
    IconSize,
    IconVariant,
} from '@mpb-com/feather/components/atoms/icon';
import { useTranslation } from 'next-i18next';
import React, {
    FunctionComponent,
    MouseEvent,
    ReactNode,
    useCallback,
} from 'react';
import styled from 'styled-components';

import { ProductCategoryVariant } from '@toucan/common/services/taxonomy/config/constants';
import { ProductCategory } from '@toucan/common/services/taxonomy/config/types';

import ChevronLeft from '@toucan/client/global/icons/chevron-left.svg';
import ChevronRight from '@toucan/client/global/icons/chevron-right.svg';

import { WrappedLink } from '@toucan/client/global/nextjs';

import {
    Link,
    LinkColor,
    LinkSize,
    LinkVariant,
} from '@toucan/client/shared/components/link';

import { styles } from './category-list-section.styles';

export type CategoryListSectionProps = {
    /**
     * Information relating to the category we are rendering
     */
    category: ProductCategory;

    /**
     * Whether or not this category is a subcategory or not
     *
     * @default false
     */
    isSubcategory?: boolean;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;

    /**
     * An event to be triggered when a category that has subcategories is clicked
     */
    onCategoryClick?: (category: ProductCategory) => void;

    /**
     * An event to be triggered when the back button is clicked
     */
    onBackClick?: () => void;
};

const StyledCategoryList = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

/**
 * Render a category 'section' in the CategoryList.
 * When the category is a subcategory a back button is displayed, along with a
 * 'Show all in xyz' link. Links with subcategories will trigger 'onCategoryClick' when
 * clicked,
 *
 * @param {CategoryListSectionProps} - The props required by the component
 * @return {ReactNode} - The category as a ReactNode
 */
export const CategoryListSection: FunctionComponent<
    CategoryListSectionProps
> = ({
    category,
    onCategoryClick,
    onBackClick,
    isSubcategory = false,
    ...otherProps
}: CategoryListSectionProps) => {
    const { t } = useTranslation();

    /**
     * Returns whether a given category has subcategories or not.
     * Will also return true when a category that is of variant 'brands' is passed.
     *
     * @param {ProductCategory} category - The category that we want to check for subcategories
     * @returns {boolean}
     */
    function categoryHasSubcategory(category: ProductCategory) {
        return (
            (category.categories && category.categories.length > 0) ||
            (category.variant &&
                category.variant === ProductCategoryVariant.BRAND)
        );
    }

    /**
     * Render the back button as a ReactNode
     *
     * @returns {ReactNode}
     */
    function renderBackButton() {
        return (
            <Link
                onClick={onBackClick}
                variant={LinkVariant.CUSTOM}
                className="back-button"
                aria-label={t('Back')}
            >
                <Icon variant={IconVariant.DECORATIVE} size={IconSize.S}>
                    <ChevronLeft />
                </Icon>
                {t('Back')}
            </Link>
        );
    }

    function renderIconButton() {
        return !isSubcategory && <ChevronRight className="list-item-icon" />;
    }

    /**
     * Render the category title as a ReactNode
     *
     * @returns {ReactNode}
     */
    const renderCategoryTitle = useCallback(() => {
        if (!category.title) {
            return;
        }

        if (!category.url) {
            return (
                <Heading className="category-list-title" size={HeadingSize.H4}>
                    {category.title}
                </Heading>
            );
        }

        return (
            <Heading size={HeadingSize.H4}>
                <WrappedLink
                    role="menuitem"
                    size={LinkSize.MEDIUM}
                    linkColor={LinkColor.NAVIGATION}
                    href={category.templatePath}
                    as={category.url}
                >
                    {category.title}
                </WrappedLink>
            </Heading>
        );
    }, [category.title]);

    /**
     * Render a list of category elements as a ReactNode
     *
     * @returns {ReactNode}
     */
    function renderCategoryList() {
        return category.categories?.map(
            (subcategory: ProductCategory, index: number): ReactNode => {
                return (
                    <li key={index}>
                        <WrappedLink
                            size={LinkSize.MEDIUM}
                            linkColor={LinkColor.NAVIGATION}
                            role="menuitem"
                            href={subcategory.templatePath}
                            as={subcategory.url}
                            className="subcategory-link"
                            onClick={(event: MouseEvent<HTMLElement>) => {
                                if (categoryHasSubcategory(subcategory)) {
                                    event.preventDefault();
                                    onCategoryClick &&
                                        onCategoryClick(subcategory);
                                }
                            }}
                            suffix={renderIconButton()}
                            suffixProps={{
                                size: IconSize.S,
                                variant: IconVariant.DECORATIVE,
                            }}
                        >
                            {subcategory.title}
                        </WrappedLink>
                    </li>
                );
            }
        );
    }

    /**
     * Render a list of categories/ brands to display as a ReactNode
     *
     * @return {ReactNode}
     */
    const renderListItems = useCallback((): ReactNode => {
        switch (category.variant) {
            case ProductCategoryVariant.LIST:
            default:
                return <ul>{renderCategoryList()}</ul>;
        }
    }, [category.variant]);

    /**
     * Render a button that will take the user to a categories page
     *
     * @return {ReactNode}
     */
    const renderShowAllButton = useCallback(() => {
        return (
            <div className="show-all-link-container">
                <Heading size={HeadingSize.H4} className="show-all-link">
                    <WrappedLink
                        role="menuitem"
                        href={category.templatePath}
                        as={category.url}
                    >
                        {t(`Show all in {{subCategoryTitle}}`, {
                            interpolation: { escapeValue: false },
                            subCategoryTitle: category.title.toLowerCase(),
                        })}
                    </WrappedLink>
                </Heading>
            </div>
        );
    }, [category.templatePath, category.url]);

    return (
        <StyledCategoryList
            category={category}
            isSubcategory={isSubcategory}
            {...otherProps}
        >
            {isSubcategory && renderBackButton()}
            {renderCategoryTitle()}
            {renderListItems()}
            {isSubcategory && category.title && renderShowAllButton()}
        </StyledCategoryList>
    );
};
